import { CSSProperties, ReactElement, useEffect, useState } from 'react'

import { Button, Input } from 'antd'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'

import { Icon } from 'components/core'
import { getOS } from 'helpers/utils'

import { Item } from './localTypes'
import * as styles from './styles.scss'

const { TextArea } = Input

interface Props {
    errorMsg?: string
    label: string
    loading: boolean
    /** Pushes a list of items  */
    push: (item: Item) => void
    style?: CSSProperties
    type: string
    validator?: (items: string[]) => boolean
    className?: string
}

const defaultValidator = (): boolean => true

const BulkAddTextArea = ({
    errorMsg,
    label = 'Element',
    loading = false,
    push,
    style,
    type,
    validator = defaultValidator,
    className = '',
}: Props): ReactElement => {
    const { t } = useTranslation('common')
    const [inputValue, setInputValue] = useState('')
    const [items, setItems] = useState<string[]>([])
    const [isValid, setIsValid] = useState(true)

    const isFieldEmpty = inputValue.length <= 0
    const enterKey = getOS() === 'Windows' ? 'Ctrl +' : '⌘ +'
    const isNullOrEmpty = (str: string): boolean => !str || !str.trim()

    useEffect(() => {
        const itemsToAdd = inputValue
            .split('\n')
            .map((value) => value.trim())
            .filter((value) => !isNullOrEmpty(value))

        setItems(() => {
            setIsValid(validator(itemsToAdd))
            return itemsToAdd
        })
    }, [inputValue, validator])

    const addItems = (): void => {
        if (isValid) {
            items.forEach((item, index) => {
                push({
                    [type]: item,
                    index,
                    id: uuid(),
                })
            })
            setInputValue('')
        }
    }

    return (
        <div
            className={`${styles['add-field-wrapper']} ${className}`}
            style={style}
        >
            <TextArea
                autoSize={{ minRows: 3, maxRows: 10 }}
                className={styles['add-field']}
                disabled={loading}
                onChange={(e) => {
                    setInputValue(e.target.value)
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.metaKey && !isFieldEmpty) {
                        e.stopPropagation()
                        addItems()
                    }
                }}
                placeholder={`Add ${label} (line separated)`}
                value={inputValue}
            />
            {!isValid && errorMsg && (
                <div className="fg-control-text is-error">{errorMsg}</div>
            )}
            <div className={styles['add-field-affix']}>
                {!isFieldEmpty && isValid && (
                    <span
                        className="fg-text-extra-light"
                        style={{ marginRight: 8 }}
                    >
                        {enterKey}{' '}
                        <span className={styles['enter-icon']}>&#9166;</span> to
                        add
                    </span>
                )}
                <Button
                    size="small"
                    icon={<Icon name="PLUS" size={13} />}
                    shape="circle"
                    type={!isFieldEmpty ? 'primary' : 'default'}
                    aria-label={t('common:add', 'Add')}
                    onClick={() => {
                        if (!isFieldEmpty) {
                            addItems()
                        }
                    }}
                    disabled={loading || !isValid}
                />
            </div>
        </div>
    )
}

export default BulkAddTextArea
